import { Box, CircularProgress } from "@mui/material";
import Alert from "@mui/material/Alert";
import { useDateRange } from "contexts/DateRangeContext";
import { useTenantTranslation } from "hooks/formatters";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { createContext, ReactNode, use, useEffect, useMemo } from "react";
import { TimeAggregation } from "src/store/api/generatedApi";
import { GroupedConsumptionSeries } from "../types";
import { useStrategyDashboardConsumption } from "./consumptionHooks";

interface ConsumptionTabTimelineContextType {
  selectedTimeAggregation: TimeAggregation | null;
  setSelectedTimeAggregation: (value: TimeAggregation) => void;
  data: GroupedConsumptionSeries; // if data is not defined, we don't show children
  isFetching: boolean;
}

const ConsumptionTabTimelineContext = createContext<
  ConsumptionTabTimelineContextType | undefined
>(undefined);

interface ConsumptionTabTimelineProviderProps {
  children: ReactNode;
}

export const ConsumptionTabTimelineProvider = ({
  children,
}: ConsumptionTabTimelineProviderProps) => {
  const { t } = useTenantTranslation();
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();

  // Default to monthly; if unavailable after load, we will correct it below
  const [selectedTimeAggregation, setSelectedTimeAggregation] =
    useStringQueryParamState<TimeAggregation | null>(
      "timeAggregation",
      "daily"
    );

  const loaded = useStrategyDashboardConsumption(
    start,
    end,
    selectedTimeAggregation ?? undefined
  );
  const data = loaded.status === "success" ? loaded.data : undefined;
  const isFetching = loaded.status === "success" ? loaded.fetching : false; // fetching while data exists
  const isError = loaded.status === "error";
  const isLoading = loaded.status === "loading" || loaded.status === "waiting";

  // If the selected time aggregation is not available in the loaded data, pick the first available
  useEffect(() => {
    if (!data || !selectedTimeAggregation) return;
    if (!data.availableTimeAggregations.has(selectedTimeAggregation)) {
      const fallback = Array.from(data.availableTimeAggregations)[0];
      if (fallback) {
        setSelectedTimeAggregation(fallback);
      }
    }
  }, [data, selectedTimeAggregation, setSelectedTimeAggregation]);

  const value: Omit<ConsumptionTabTimelineContextType, "data"> & {
    data: GroupedConsumptionSeries | undefined;
  } = useMemo(
    () => ({
      selectedTimeAggregation,
      setSelectedTimeAggregation: (value: TimeAggregation) => {
        setSelectedTimeAggregation(value);
      },
      data,
      isFetching,
    }),
    [selectedTimeAggregation, setSelectedTimeAggregation, data, isFetching]
  );

  if (isLoading) {
    return (
      <Box p={16}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError) {
    return <Alert severity="error">{t("errorLoadingData")}</Alert>;
  }
  if (!data) {
    return <Alert severity="error">{t("errorLoadingData")}</Alert>;
  }

  return (
    <ConsumptionTabTimelineContext
      value={value as ConsumptionTabTimelineContextType}
    >
      {children}
    </ConsumptionTabTimelineContext>
  );
};

export const useConsumptionTabTimeline = () => {
  const context = use(ConsumptionTabTimelineContext);
  if (context === undefined) {
    throw new Error(
      "useConsumptionTab must be used within a ConsumptionTabTimelineProvider"
    );
  }
  return context;
};
